import React, { useState } from 'react';
import { supabase } from '../supabase.js';

// ─────────────────────────────────────────────────────────────
// Shared iOS 26 Glass helpers (local — no theme.jsx import to
// keep this file self-contained for the pre-auth flow)
// ─────────────────────────────────────────────────────────────
const F = "-apple-system, BlinkMacSystemFont, 'SF Pro Display', 'Segoe UI', sans-serif";

const glassCard = (dark) => ({
  background: dark ? "rgba(28,30,38,0.78)" : "rgba(255,255,255,0.72)",
  backdropFilter: "blur(40px) saturate(180%)",
  WebkitBackdropFilter: "blur(40px) saturate(180%)",
  border: dark ? "1px solid rgba(255,255,255,0.13)" : "1px solid rgba(255,255,255,0.85)",
  boxShadow: dark
    ? "0 24px 64px rgba(0,0,0,0.55), inset 0 1px 0 rgba(255,255,255,0.08)"
    : "0 24px 64px rgba(0,0,0,0.14), inset 0 1px 0 rgba(255,255,255,0.95)",
});

const backBtnStyle = (dark) => ({
  background: dark ? "rgba(255,255,255,0.10)" : "rgba(0,0,0,0.06)",
  backdropFilter: "blur(20px)",
  WebkitBackdropFilter: "blur(20px)",
  border: dark ? "1px solid rgba(255,255,255,0.12)" : "1px solid rgba(255,255,255,0.7)",
  boxShadow: dark
    ? "inset 0 1px 0 rgba(255,255,255,0.08)"
    : "inset 0 1px 0 rgba(255,255,255,0.9)",
  borderRadius: 17, width: 34, height: 34,
  cursor: "pointer", padding: 0, marginBottom: 20,
  display: "flex", alignItems: "center", justifyContent: "center",
  color: dark ? "rgba(235,235,245,0.65)" : "rgba(60,60,67,0.6)",
  transition: "transform 0.22s cubic-bezier(0.34,1.2,0.64,1)",
});

const AUTH_ANIM = `
@keyframes authFadeUp {
  0%   { opacity: 0; transform: translateY(20px) scale(0.97); filter: blur(6px); }
  55%  { filter: blur(0); }
  100% { opacity: 1; transform: translateY(0) scale(1); filter: blur(0); }
}
.auth-btn:active { transform: scale(0.97) !important; }
`;

// ─────────────────────────────────────────────────────────────
// AuthScreen
// ─────────────────────────────────────────────────────────────
function AuthScreen({ onAuth }) {
  const [mode, setMode] = useState("login"); // "login" | "register" | "magic"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [magicSent, setMagicSent] = useState(false);

  const dark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg   = dark ? "#0D0D0E" : "#EEF3FB";
  const text = dark ? "#fff" : "#1a1a1a";
  const sub  = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp  = dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.055)";
  const inpBorder = dark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.8)";

  async function handleLogin(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { data, error: err } = await supabase.auth.signInWithPassword({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    onAuth(data.session);
  }

  async function handleRegister(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { data, error: err } = await supabase.auth.signUp({ email, password });
    setLoading(false);
    if (err) { setError(err.message); return; }
    if (data.session) { onAuth(data.session); return; }
    setError("Konto erstellt! Bitte E-Mail bestätigen, dann einloggen.");
    setMode("login");
  }

  async function handleMagicLink(e) {
    e.preventDefault();
    setLoading(true); setError("");
    const { error: err } = await supabase.auth.signInWithOtp({ email });
    setLoading(false);
    if (err) { setError(err.message); return; }
    setMagicSent(true);
  }

  const inputStyle = {
    width: "100%", boxSizing: "border-box", padding: "13px 16px",
    background: inp,
    border: `1px solid ${inpBorder}`,
    borderRadius: 14,
    color: text, fontSize: 16, fontFamily: F, outline: "none",
    backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
    boxShadow: dark
      ? "inset 0 1px 0 rgba(255,255,255,0.06)"
      : "inset 0 1px 0 rgba(255,255,255,0.85)",
  };
  const btnPrimary = {
    width: "100%", padding: "15px",
    background: "var(--accent-online)", border: "none", borderRadius: 20,
    color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: F,
    cursor: "pointer",
    boxShadow: "0 4px 20px rgba(55,138,221,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
    opacity: loading ? 0.7 : 1,
    transition: "transform 0.22s cubic-bezier(0.34,1.2,0.64,1)",
  };
  const btnGhost = {
    background: "none", border: "none", color: "var(--accent-online)",
    fontFamily: F, fontSize: 14, fontWeight: 500, cursor: "pointer", padding: "6px 0",
  };

  return (
    <>
      <style>{AUTH_ANIM}</style>
      <div style={{
        position: "fixed", inset: 0, background: bg,
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: 20, fontFamily: F, overflowY: "auto",
      }}>
        <div style={{
          width: "100%", maxWidth: 380,
          ...glassCard(dark),
          borderRadius: 28, padding: "36px 28px",
          animation: "authFadeUp 0.46s cubic-bezier(0.34,1.2,0.64,1) both",
        }}>
          <div style={{ textAlign: "center", marginBottom: 28 }}>
            {/* Logo */}
            <div style={{
              width: 68, height: 68, borderRadius: 20, margin: "0 auto 14px",
              overflow: "hidden",
            }}>
              <img src="/boothly-logo.png" alt="Boothly" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
            </div>
            <div style={{ fontSize: 24, fontWeight: 800, color: text, letterSpacing: "-0.6px" }}>Boothly</div>
            <div style={{ fontSize: 14, color: sub, marginTop: 4 }}>
              {mode === "login" ? "Anmelden" : mode === "register" ? "Konto erstellen" : "Magic Link"}
            </div>
          </div>

          {magicSent ? (
            <div style={{ textAlign: "center", color: text, lineHeight: 1.5 }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>📬</div>
              <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 17 }}>Link gesendet</div>
              <div style={{ fontSize: 14, color: sub }}>Klicke auf den Link in deiner E-Mail, um dich anzumelden.</div>
              <button className="auth-btn" style={{ ...btnGhost, marginTop: 20 }} onClick={() => setMagicSent(false)}>Zurück</button>
            </div>
          ) : (
            <form onSubmit={mode === "login" ? handleLogin : mode === "register" ? handleRegister : handleMagicLink}>
              <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <input type="email" placeholder="E-Mail" value={email} onChange={e => setEmail(e.target.value)} required style={inputStyle} />
                {mode !== "magic" && (
                  <input type="password" placeholder="Passwort" value={password} onChange={e => setPassword(e.target.value)} required style={inputStyle} />
                )}
                {error && (
                  <div style={{ fontSize: 13, color: "var(--accent-red)", padding: "9px 13px", background: "rgba(255,69,58,0.1)", borderRadius: 12, border: "1px solid rgba(255,69,58,0.18)" }}>
                    {error}
                  </div>
                )}
                <button className="auth-btn" type="submit" style={{ ...btnPrimary, marginTop: 4 }} disabled={loading}>
                  {loading ? "..." : mode === "login" ? "Anmelden" : mode === "register" ? "Konto erstellen" : "Magic Link senden"}
                </button>
              </div>
              <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 4, marginTop: 16 }}>
                {mode === "login" && (<>
                  <button type="button" style={btnGhost} onClick={() => { setMode("register"); setError(""); }}>Noch kein Konto? Registrieren</button>
                  <button type="button" style={btnGhost} onClick={() => { setMode("magic"); setError(""); }}>Mit Magic Link anmelden</button>
                </>)}
                {mode !== "login" && (
                  <button type="button" style={btnGhost} onClick={() => { setMode("login"); setError(""); }}>Zurück zum Login</button>
                )}
              </div>
            </form>
          )}
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// ModeHubStub — Platzhalter-Hub (Trödel)
// ─────────────────────────────────────────────────────────────
function ModeHubStub({ title, subtitle, accentColor, onBack }) {
  const dark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg   = dark ? "#0D0D0E" : "#EEF3FB";
  const text = dark ? "#fff" : "#1a1a1a";
  const sub  = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const border = dark ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.06)";

  return (
    <>
      <style>{AUTH_ANIM}</style>
      <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
        <div style={{
          width: "100%", maxWidth: 380,
          ...glassCard(dark),
          borderRadius: 28, padding: "28px 26px",
          animation: "authFadeUp 0.46s cubic-bezier(0.34,1.2,0.64,1) both",
        }}>
          <button className="auth-btn" onClick={onBack} aria-label="Zurück" style={backBtnStyle(dark)}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{
            width: 56, height: 56, borderRadius: 17, marginBottom: 16,
            background: accentColor.weak, color: accentColor.solid,
            display: "flex", alignItems: "center", justifyContent: "center",
            boxShadow: `inset 0 1px 0 rgba(255,255,255,0.2)`,
          }}>
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
              <path d="M12 2L2 7v10l10 5l10-5V7L12 2z"/><path d="M2 7l10 5"/><path d="M22 7l-10 5"/><path d="M12 12V22"/>
            </svg>
          </div>
          <div style={{ fontSize: 22, fontWeight: 800, color: text, letterSpacing: "-0.5px", marginBottom: 6 }}>{title}</div>
          <div style={{ fontSize: 14, color: sub, lineHeight: 1.55, marginBottom: 22 }}>{subtitle}</div>
          <div style={{
            padding: "12px 15px",
            background: dark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
            border: `1px solid ${border}`, borderRadius: 14,
            fontSize: 13, color: sub, lineHeight: 1.5,
          }}>
            Dieser Bereich wird gerade gebaut. Hier kannst du bald deine Markt-Tage tracken.
          </div>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// CreateStoreScreen
// ─────────────────────────────────────────────────────────────
function CreateStoreScreen({ token, onCreated, onBack }) {
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg   = dark ? "#0D0D0E" : "#EEF3FB";
  const text = dark ? "#fff" : "#1a1a1a";
  const sub  = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp  = dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.055)";
  const inpBorder = dark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.8)";

  async function handleCreate(e) {
    e.preventDefault();
    if (!name.trim()) return;
    setLoading(true); setError("");
    try {
      const res = await fetch("/api/stores", {
        method: "POST",
        headers: { "Content-Type": "application/json", "Authorization": `Bearer ${token}` },
        body: JSON.stringify({ name: name.trim() }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const store = await res.json();
      onCreated(store);
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  return (
    <>
      <style>{AUTH_ANIM}</style>
      <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
        <div style={{
          width: "100%", maxWidth: 380,
          ...glassCard(dark),
          borderRadius: 28, padding: "32px 28px",
          animation: "authFadeUp 0.46s cubic-bezier(0.34,1.2,0.64,1) both",
        }}>
          <button className="auth-btn" onClick={onBack} aria-label="Zurück" style={backBtnStyle(dark)}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ fontSize: 22, fontWeight: 800, color: text, marginBottom: 6, letterSpacing: "-0.5px" }}>Neuen Store erstellen</div>
          <div style={{ fontSize: 14, color: sub, marginBottom: 24 }}>Du wirst automatisch Inhaber dieses Stores.</div>
          <form onSubmit={handleCreate}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="text" placeholder="Store-Name" value={name}
                onChange={e => setName(e.target.value)} required autoFocus
                style={{
                  width: "100%", boxSizing: "border-box", padding: "13px 16px",
                  background: inp, border: `1px solid ${inpBorder}`, borderRadius: 14,
                  color: text, fontSize: 16, fontFamily: F, outline: "none",
                  boxShadow: dark ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.85)",
                }}
              />
              {error && <div style={{ fontSize: 13, color: "var(--accent-red)", padding: "9px 13px", background: "rgba(255,69,58,0.1)", borderRadius: 12, border: "1px solid rgba(255,69,58,0.18)" }}>{error}</div>}
              <button className="auth-btn" type="submit" disabled={loading || !name.trim()} style={{
                width: "100%", padding: "15px", background: "var(--accent-online)", border: "none",
                borderRadius: 20, color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: F,
                cursor: "pointer", opacity: (loading || !name.trim()) ? 0.6 : 1,
                boxShadow: "0 4px 20px rgba(55,138,221,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
                transition: "transform 0.22s cubic-bezier(0.34,1.2,0.64,1)",
                marginTop: 4,
              }}>{loading ? "..." : "Store erstellen"}</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

// ─────────────────────────────────────────────────────────────
// JoinStoreScreen
// ─────────────────────────────────────────────────────────────
function JoinStoreScreen({ token, onJoined, onBack }) {
  const [code, setCode] = useState("");
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const dark = window.matchMedia?.("(prefers-color-scheme: dark)")?.matches;
  const bg   = dark ? "#0D0D0E" : "#EEF3FB";
  const text = dark ? "#fff" : "#1a1a1a";
  const sub  = dark ? "rgba(255,255,255,0.45)" : "rgba(0,0,0,0.45)";
  const inp  = dark ? "rgba(255,255,255,0.09)" : "rgba(0,0,0,0.055)";
  const inpBorder = dark ? "rgba(255,255,255,0.12)" : "rgba(255,255,255,0.8)";

  async function lookupCode(c) {
    const clean = c.toUpperCase().replace(/[^A-Z0-9]/g, "");
    setCode(clean);
    setPreview(null); setError("");
    if (clean.length < 6) return;
    try {
      const res = await fetch(`/api/invitations/${clean}`);
      if (res.status === 404) { setError("Ungültiger Code"); return; }
      if (res.status === 410) { setError("Dieser Code ist abgelaufen"); return; }
      if (!res.ok) return;
      setPreview(await res.json());
    } catch {}
  }

  async function handleJoin(e) {
    e.preventDefault();
    if (!code) return;
    setLoading(true); setError("");
    try {
      const res = await fetch(`/api/invitations/${code}/redeem`, {
        method: "POST",
        headers: { "Authorization": `Bearer ${token}` },
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || `HTTP ${res.status}`); }
      const data = await res.json();
      onJoined({ ...data.store, role: data.role });
    } catch (e) { setError(e.message); }
    setLoading(false);
  }

  const ROLE_LABELS = { admin: "Admin", fulfillment: "Fulfillment", finance: "Finanzen", viewer: "Betrachter" };

  return (
    <>
      <style>{AUTH_ANIM}</style>
      <div style={{ minHeight: "100vh", background: bg, display: "flex", alignItems: "center", justifyContent: "center", padding: 20, fontFamily: F }}>
        <div style={{
          width: "100%", maxWidth: 380,
          ...glassCard(dark),
          borderRadius: 28, padding: "32px 28px",
          animation: "authFadeUp 0.46s cubic-bezier(0.34,1.2,0.64,1) both",
        }}>
          <button className="auth-btn" onClick={onBack} aria-label="Zurück" style={backBtnStyle(dark)}>
            <svg width="13" height="13" viewBox="0 0 14 14" fill="none">
              <path d="M9 2L3 7L9 12" stroke="currentColor" strokeWidth="2.1" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <div style={{ fontSize: 22, fontWeight: 800, color: text, marginBottom: 6, letterSpacing: "-0.5px" }}>Store beitreten</div>
          <div style={{ fontSize: 14, color: sub, marginBottom: 24 }}>Gib den Einladungscode ein, den du erhalten hast.</div>
          <form onSubmit={handleJoin}>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              <input
                type="text" placeholder="Einladungscode (z.B. A1B2C3D4)"
                value={code} onChange={e => lookupCode(e.target.value)}
                maxLength={10} autoFocus
                style={{
                  width: "100%", boxSizing: "border-box", padding: "13px 16px",
                  background: inp, border: `1px solid ${inpBorder}`, borderRadius: 14,
                  color: text, fontSize: 17, fontFamily: "monospace, monospace",
                  textTransform: "uppercase", letterSpacing: "0.12em", outline: "none",
                  boxShadow: dark ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "inset 0 1px 0 rgba(255,255,255,0.85)",
                }}
              />
              {preview && (
                <div style={{ padding: "12px 14px", background: "rgba(48,209,88,0.1)", borderRadius: 14, border: "1px solid rgba(48,209,88,0.25)" }}>
                  <div style={{ fontSize: 13, color: "var(--accent-stand)", fontWeight: 700 }}>Einladung gefunden</div>
                  <div style={{ fontSize: 14, color: text, marginTop: 2 }}>{preview.store_name}</div>
                  <div style={{ fontSize: 12, color: sub, marginTop: 1 }}>Rolle: {ROLE_LABELS[preview.role] || preview.role}</div>
                </div>
              )}
              {error && <div style={{ fontSize: 13, color: "var(--accent-red)", padding: "9px 13px", background: "rgba(255,69,58,0.1)", borderRadius: 12, border: "1px solid rgba(255,69,58,0.18)" }}>{error}</div>}
              <button className="auth-btn" type="submit" disabled={loading || !code} style={{
                width: "100%", padding: "15px", background: "var(--accent-online)", border: "none",
                borderRadius: 20, color: "#fff", fontSize: 16, fontWeight: 700, fontFamily: F,
                cursor: "pointer", opacity: (loading || !code) ? 0.6 : 1,
                boxShadow: "0 4px 20px rgba(55,138,221,0.4), inset 0 1px 0 rgba(255,255,255,0.25)",
                transition: "transform 0.22s cubic-bezier(0.34,1.2,0.64,1)",
                marginTop: 4,
              }}>{loading ? "..." : "Beitreten"}</button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
}

export { AuthScreen, ModeHubStub, CreateStoreScreen, JoinStoreScreen };
